/**
 * Pure helpers rond de beeldreeks van een social post.
 *
 * Geen supabase-import: dit bestand moet los te testen zijn. De kant die de
 * database en storage raakt staat in socialImageStorage.ts.
 *
 * LET OP: de Deno-kant van de verzendtak heeft een eigen, kleinere variant in
 * supabase/functions/shared/socialImages.ts. Die twee runtimes delen geen
 * modules; niet samenvoegen.
 */

/** Instagram accepteert er niet meer dan tien; we hanteren die grens overal. */
export const MAX_SOCIAL_IMAGES = 10;

export interface SocialImage {
  url: string;
  /** Pad binnen de bucket blog-images; leeg voor beelden van buiten storage. */
  storage_path: string;
}

/** Leest de JSONB-kolom `images` uit. Tolerant: rommel levert een lege lijst. */
export function parseImages(raw: unknown): SocialImage[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const record = item as Record<string, unknown>;
    if (typeof record.url !== 'string' || !record.url) return [];
    return [
      {
        url: record.url,
        storage_path: typeof record.storage_path === 'string' ? record.storage_path : '',
      },
    ];
  });
}

/**
 * De invariant uit de spec: image_url is altijd het eerste beeld uit de reeks.
 * Bij een lege reeks blijft het losse beeld leidend.
 */
export function coverUrl(images: SocialImage[], fallback: string | null): string | null {
  return images.length > 0 ? images[0].url : fallback;
}

/**
 * Verplaatst het beeld op index `from` naar index `to`. Bij een ongeldige index
 * of `from === to` blijft de array-referentie gelijk, zodat React niet onnodig
 * hertekent.
 */
export function moveImage(images: SocialImage[], from: number, to: number): SocialImage[] {
  if (from < 0 || from >= images.length) return images;
  if (to < 0 || to >= images.length) return images;
  if (from === to) return images;
  const next = [...images];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/**
 * Kiest de reeks die een nieuwe post van dezelfde blog overneemt: de nieuwste
 * eerdere post met meer dan één beeld. Eén beeld is de standaardsituatie en
 * levert niets op om over te nemen.
 *
 * Bij een gelijke `created_at` beslist de volgorde in `rows`:
 * Array.prototype.sort is stabiel, dus de rij die eerder in de invoer stond
 * wint. Dat is stabiel maar willekeurig, want die volgorde komt uit een
 * database-query zonder tweede sorteersleutel. Die sleutel is bewust niet
 * toegevoegd: posts worden per kanaal los aangemaakt, dus gelijke tijdstempels
 * komen praktisch niet voor.
 */
export function pickInheritedImages(
  rows: Array<{ images: unknown; created_at: string }>,
): SocialImage[] {
  const candidates = rows
    .map((row) => ({ images: parseImages(row.images), created_at: row.created_at }))
    .filter((row) => row.images.length > 1)
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  return candidates.length > 0 ? candidates[0].images : [];
}
